import React, {useState} from "react";

import FlexBox from "view/components/Layout/FlexBox";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import {
  Box,
  Button,
  Link,
  ListItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {isCommentEmpty} from "tools/comment/helpers";
import {
  AttachFile,
  ErrorOutline,
  ReportProblemOutlined,
} from "@material-ui/icons";
import CommentText from "view/components/TaskCommentsSection/Comment/CommentText";
import {sanitizeHtml} from "view/components/SafeHtml";
import formatDate from "tools/formats/dayJsFormat";
import InfoIcon from "@material-ui/icons/Info";
import {orange} from "@material-ui/core/colors";
import List from "@material-ui/core/List";
import {
  INotificationBar,
  INotificationMetaDataProductBulkImport,
  INotificationMetaDataProductBulkImportError,
  UserNotificationStatus,
  UserNotificationType,
} from "@evennt-tech/api-v2-core";
import {
  INotificationMetaDataExhibitorInfoUpdateUser,
  INotificationMetaDataTaskComment,
} from "@evennt-tech/api-v2-core/dist/notification-bar/notification-bar.interface";
import useStyles from "./styles";

const MAX_VISIBLE_PRODUCT_IMPORT_ERRORS = 3;

// The api starts an image line with `Image` or `Image_2`; links are long,
// so that label links to the image instead of the line quoting it.
const IMAGE_LABEL = /^Image(?:_\d+)?/;
const WEB_LINK = /^https?:\/\//i;

/** `https://cdn.example.com/…/chair.jpg`: the host and the file name. */
const shortenLink = (url: string): string => {
  try {
    const {origin, pathname} = new URL(url);
    const segments = pathname.split("/").filter(Boolean);

    return segments.length > 1 ? `${origin}/…/${segments.pop()}` : url;
  } catch {
    return url;
  }
};

const ProductImportErrorMessage = ({
  message,
  url,
}: INotificationMetaDataProductBulkImportError) => {
  const [label] = message.match(IMAGE_LABEL) ?? [];

  if (!label || !url || !WEB_LINK.test(url)) {
    return <>{message}</>;
  }

  return (
    <>
      <Tooltip title={shortenLink(url)}>
        <Link href={url} target="_blank" rel="noopener noreferrer">
          {label}
        </Link>
      </Tooltip>
      {message.slice(label.length)}
    </>
  );
};

type Props = {
  notification: INotificationBar;
  observeNotification: (node: HTMLLIElement | null) => void;
  onTaskOpen: () => void;
  dateFormat: string;
};
const ProductBulkImportContent = ({
  title,
  metadata,
}: {
  title: string;
  metadata?: INotificationMetaDataProductBulkImport;
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  if (!metadata) {
    return null;
  }

  const {outcome, errors = [], totalRows, createdCount, failedCount} = metadata;

  const hasMoreErrors = errors.length > MAX_VISIBLE_PRODUCT_IMPORT_ERRORS;
  const visibleCount = expanded
    ? errors.length
    : MAX_VISIBLE_PRODUCT_IMPORT_ERRORS;
  const failedErrors = errors.filter(({productImported}) => !productImported);
  const importedErrors = errors.filter(({productImported}) => productImported);
  const visibleFailed = failedErrors.slice(0, visibleCount);
  const errorCount = `${importedErrors.length} ${
    importedErrors.length === 1 ? "error" : "errors"
  }`;
  // Headings only once a row was imported with an error: a list of failed
  // rows alone needs none.
  const errorGroups: [
    string | null,
    INotificationMetaDataProductBulkImportError[]
  ][] = importedErrors.length
    ? [
        ["Failed:", visibleFailed],
        [
          "Completed with errors:",
          importedErrors.slice(0, visibleCount - visibleFailed.length),
        ],
      ]
    : [[null, visibleFailed]];
  const isWarning = outcome === "partiallyFailed";
  const StatusIcon = isWarning ? ReportProblemOutlined : ErrorOutline;

  return (
    <FlexBox gap={1} column fullWidth>
      <Typography
        variant="body2"
        className={`${classes.notificationTitle} ${classes.statusLine} ${
          isWarning
            ? classes.notificationTitleWarning
            : classes.notificationTitleError
        }`}
      >
        <StatusIcon className={classes.titleIcon} />
        <span>
          {title}
          {isWarning && (
            <>
              <br />
              {`${createdCount} of ${totalRows} products imported. `}
              <span
                className={classes.counter}
              >{`${failedCount} failed.`}</span>{" "}
              <span className={classes.counter}>{`${errorCount}.`}</span>
            </>
          )}
        </span>
      </Typography>

      {outcome === "didNotComplete" ? (
        <Typography variant="body2" className={classes.notificationText}>
          The import could not be completed. Please try again. If the problem
          persists, contact support@ffair.io.
        </Typography>
      ) : (
        <>
          {outcome === "fullyFailed" && (
            <Typography variant="body2" className={classes.notificationText}>
              No products were imported.
            </Typography>
          )}

          {errorGroups.map(
            ([heading, groupErrors]) =>
              !!groupErrors.length && (
                <React.Fragment key={heading ?? "errors"}>
                  {heading && (
                    <Typography
                      variant="body2"
                      className={classes.errorGroupTitle}
                    >
                      {heading}
                    </Typography>
                  )}
                  <List disablePadding>
                    {groupErrors.map((error, idx) => (
                      <ListItem
                        key={idx}
                        disableGutters
                        className={classes.contentItem}
                      >
                        <span>
                          Row {error.rowNumber}:{" "}
                          <ProductImportErrorMessage {...error} />
                        </span>
                      </ListItem>
                    ))}
                  </List>
                </React.Fragment>
              )
          )}

          {hasMoreErrors && (
            <Button
              className={classes.notificationButton}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Show less" : "Show all errors"}
            </Button>
          )}
        </>
      )}
    </FlexBox>
  );
};

const NotificationItem = ({
  notification,
  observeNotification,
  onTaskOpen,
  dateFormat,
}: Props) => {
  const classes = useStyles();
  const {title, hasAttachments, content, createdAt, type} = notification;
  const isDeletedTask =
    type === UserNotificationType.TaskComment &&
    !!(notification as INotificationBar<
      string,
      INotificationMetaDataTaskComment
    >).metadata?.taskDeletedAt;
  const productBulkImportFileName =
    type === UserNotificationType.ProductBulkImport
      ? (notification as INotificationBar<
          string,
          INotificationMetaDataProductBulkImport
        >).metadata?.fileName
      : undefined;

  const NotificationTitle = () => {
    if (hasAttachments && !isCommentEmpty(content)) {
      return (
        <FlexBox alignCenter gap={1}>
          <Typography className={classes.notificationTitle}>
            <AttachFile className={classes.fileIcon} />
            {title}
          </Typography>
          {isDeletedTask && <DeletedStandTip />}
        </FlexBox>
      );
    }
    if (productBulkImportFileName) {
      return (
        <FlexBox alignCenter gap={1}>
          <Typography className={classes.notificationTitle} variant="body2">
            Bulk import: File {productBulkImportFileName}
          </Typography>
        </FlexBox>
      );
    }
    return (
      <FlexBox alignCenter gap={1}>
        <Typography className={classes.notificationTitle} variant="body2">
          {title}
        </Typography>
        {isDeletedTask && <DeletedStandTip />}
      </FlexBox>
    );
  };
  const NotificationContent = () => {
    if (type === UserNotificationType.TaskComment) {
      if (isCommentEmpty(content)) {
        return (
          <Typography className={classes.attachmentInfo}>
            New file(s) attached to the Comment.
          </Typography>
        );
      }
      return (
        <Box className={classes.truncatedContent}>
          <CommentText text={content} />
        </Box>
      );
    }
    if (type === UserNotificationType.Visit) {
      return (
        <Typography variant="body2" className={classes.notificationText}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                notification.content.replaceAll(/\n|\\n/g, "<br />")
              ),
            }}
          />
        </Typography>
      );
    }
    if (type === UserNotificationType.Aditus) {
      return (
        <Typography variant="body2" className={classes.notificationText}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                notification.content.replaceAll(/\n|\\n/g, "<br />")
              ),
            }}
          />
        </Typography>
      );
    }
    if (type === UserNotificationType.BulkUpload) {
      return (
        <Typography variant="body2" className={classes.notificationText}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                notification.content.replaceAll(/\n|\\n/g, "<br />")
              ),
            }}
          />
        </Typography>
      );
    }
    if (type === UserNotificationType.Jet) {
      return (
        <Typography variant="body2" className={classes.notificationText}>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                notification.content.replaceAll(/\n|\\n/g, "<br />")
              ),
            }}
          />
        </Typography>
      );
    }
    if (type === UserNotificationType.ProductBulkImport) {
      return (
        <ProductBulkImportContent
          title={title}
          metadata={
            (notification as INotificationBar<
              string,
              INotificationMetaDataProductBulkImport
            >).metadata
          }
        />
      );
    }
    if (type === UserNotificationType.ExhibitorInfoUpdate) {
      return (
        <FlexBox gap={3} column fullWidth>
          <Typography variant="h4">
            <strong>
              {
                (notification as INotificationBar<
                  string,
                  INotificationMetaDataExhibitorInfoUpdateUser
                >).metadata?.exhibitorName
              }
            </strong>{" "}
            requested changes:
          </Typography>

          <List disablePadding>
            {(notification as INotificationBar<
              string,
              INotificationMetaDataExhibitorInfoUpdateUser
            >).metadata?.values?.map(
              ({fieldName, currentValue, newValue}, idx) => (
                <ListItem
                  key={idx}
                  disableGutters
                  className={classes.contentItem}
                >
                  <>
                    {fieldName}: {currentValue} &rarr;{" "}
                    <strong>{newValue}</strong>
                  </>
                </ListItem>
              )
            )}
          </List>
        </FlexBox>
      );
    }
    return null;
  };
  const formatedCreatedAt = formatDate(createdAt, dateFormat);
  return (
    <ListItem
      className={
        notification.status === UserNotificationStatus.Read
          ? classes.notificationRead
          : classes.notificationUnread
      }
      ref={(node) => node && observeNotification(node)}
      data-id={notification.id}
      data-status={notification.status}
    >
      <FlexBox width="100%">
        <FlexBox alignItems="flex-start" mr={1}>
          <FiberManualRecordIcon
            className={
              notification.status === UserNotificationStatus.Read
                ? classes.notificationIconRead
                : classes.notificationIconUnread
            }
          />
        </FlexBox>
        <FlexBox width="100%" flexDirection="column">
          {NotificationTitle()}
          <Box mt={1}>{NotificationContent()}</Box>
          <FlexBox justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" className={classes.notificationDate}>
              {formatedCreatedAt}
            </Typography>
            {UserNotificationType.TaskComment === type && (
              <Button
                className={classes.notificationButton}
                onClick={onTaskOpen}
                disabled={isDeletedTask}
              >
                Open Task
              </Button>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    </ListItem>
  );
};

function DeletedStandTip() {
  return (
    <Tooltip title="This exhibitor was removed">
      <InfoIcon width={14} htmlColor={orange.A400} />
    </Tooltip>
  );
}

export default NotificationItem;
